import type { CountryCode } from "libphonenumber-js";

import { InternationalMdnInput } from "@expert/common-ui";
import { Button, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { MdnInput, RadioCard } from "@/common-ui";
import { useCallbacksRemaining } from "@/sdk";

import type { CallbackSurface } from "../../types";

import { useCallbacksFormStore } from "../../callbacksForm.store";
import { callbackActionConfig } from "../../utilities";
import styles from "./DynamicCallbackForm.module.css";
import { SecondsInput } from "./Inputs";
import { useScheduleCallbackForm } from "./useScheduleCallbackForm";

const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

const CALLBACK_REASONS = [
    {
        id: "dynamic-callback-reason-dropped",
        value: "call-dropped-prematurely",
        label: "Call Dropped Prematurely",
        actionId: "DynamicCallbackForm_CallDroppedPrematurely",
    },
    {
        id: "dynamic-callback-reason-other",
        value: "other",
        label: "Other",
        actionId: "DynamicCallbackForm_Other",
    },
] as const;

type CallbackReason = (typeof CALLBACK_REASONS)[number]["value"];

interface Props {
    callbackSurface: CallbackSurface;
    afterSubmitClickDefault?: () => void;
    reachedCallbackCutoff?: boolean;
    isInternationalPhoneEnabled?: boolean;
    defaultCountry?: CountryCode;
}

function applyDynamicCallbackDefaults() {
    useCallbacksFormStore.getState().setCallbackType("callback-user-with-delay");
}

/**
 * Wrap-up callback form. Schedules through the same callback state as `ScheduleCallbackForm`,
 * with a delay stepper. The selected reason category is recorded on `CallbackScheduled`.
 */
export function DynamicCallbackForm({
    callbackSurface,
    afterSubmitClickDefault,
    reachedCallbackCutoff,
    isInternationalPhoneEnabled,
    defaultCountry,
}: Props) {
    const callbackType = useCallbacksFormStore((state) => state.callbackType);
    const selectedCallbackNowDelay = useCallbacksFormStore((state) => state.selectedCallbackNowDelay);
    const { canCallback } = useCallbacksRemaining();
    const [reason, setReason] = useState<CallbackReason | null>(null);
    const [otherReason, setOtherReason] = useState("");

    const { selectedCallbackMdn, defaultMdn, isSubmitting, isCallbackMdnValid, onSubmit, onMdnInput } =
        useScheduleCallbackForm({
            callbackSurface,
            afterSubmitClickDefault,
            defaultCountry,
            applyDefaults: applyDynamicCallbackDefaults,
            callbackReason: reason ?? undefined,
        });

    const mdnInputProps = {
        id: "dynamic-callback-number",
        value: selectedCallbackMdn,
        onInput: onMdnInput,
        autoFocus: true,
        size: "sm" as const,
        variant: "filled" as const,
        defaultCountry,
        defaultValue: defaultMdn,
        classNames: { input: styles.fieldInput },
    };

    const scheduleDisabled =
        isSubmitting ||
        !canCallback ||
        reachedCallbackCutoff ||
        !isCallbackMdnValid ||
        reason === null ||
        (reason === "other" && otherReason.trim().length === 0);

    return (
        <Stack gap="lg">
            <Stack gap="3xs">
                <Text className={styles.fieldLabel} component="label" htmlFor="dynamic-callback-number">
                    Callback number
                </Text>
                {isInternationalPhoneEnabled ? (
                    <InternationalMdnInput {...mdnInputProps} />
                ) : (
                    <MdnInput {...mdnInputProps} />
                )}
            </Stack>

            <Stack gap="3xs">
                <Text className={styles.fieldLabel} component="label" htmlFor="dynamic-callback-time">
                    Callback time
                </Text>
                <SecondsInput
                    id="dynamic-callback-time"
                    fullWidth
                    suffix=" seconds"
                    variant="filled"
                    classNames={{ input: styles.fieldInput, control: styles.delayControl }}
                />
            </Stack>

            <Stack gap="xs">
                <Stack gap={0}>
                    <Text className={styles.sectionLabel}>Reason for callback</Text>
                    <Text className={styles.description}>This callback will be tracked against this interaction.</Text>
                </Stack>
                <Stack className={styles.optionList} gap="xs">
                    {CALLBACK_REASONS.map((option) => (
                        <RadioCard
                            key={option.value}
                            id={option.id}
                            fullWidth
                            color="var(--mantine-color-dark-5)"
                            radioColor="asurion.4"
                            label={option.label}
                            selected={reason === option.value}
                            analytics={{ actionId: option.actionId }}
                            onClick={() => setReason(option.value)}
                        />
                    ))}
                </Stack>
                {reason === "other" ? (
                    <TextInput
                        id="dynamic-callback-other-reason-input"
                        label="Reason"
                        variant="filled"
                        value={otherReason}
                        onChange={(event) => setOtherReason(event.currentTarget.value)}
                        data-testid="dynamic-callback-other-reason"
                        className="fs-mask"
                        classNames={{ label: styles.fieldLabel, input: styles.fieldInput }}
                    />
                ) : null}
            </Stack>

            <AnalyticsButton
                analytics={{
                    actionId: callbackActionConfig.schedule.actionId,
                    eventData: {
                        callbackType,
                        callbackDelay: selectedCallbackNowDelay,
                    },
                }}
                className={styles.scheduleButton}
                fullWidth
                disabled={scheduleDisabled}
                loading={isSubmitting}
                onClick={() => void onSubmit()}
                data-testid={callbackActionConfig.schedule.testId}
            >
                Schedule
            </AnalyticsButton>
        </Stack>
    );
}
